import { calculateCostPerAction, calDifferenceRelativeToPrevious } from 'shared/utils'
import { FIRSTSUBHEADER, SECONDSUBHEADER } from './constants'
import { type Row, type IdType } from 'react-table'
import { type FormatIds } from 'shared/numberFormat'

export const DEFAULT_DATA_COLUMN_PROPERTIES = {
  sortDescFirst: true
}

interface ROASColumnSortTypeFn {
  subHeader: string
  ratioFn: (numerator: number, denominator: number) => number
  id: FormatIds
  currencyCode: string
}
interface CPAColumnSortTypeFn {
  subHeader: string
  id: FormatIds
  currencyCode: string
}

interface CustomRatioDifferenceColumnSortTypeFn {
  numeratorColumnId: string
  denominatorColumnId: string
  ratioFn: (numerator: number, denominator: number) => number
}

interface BaseDifferenceColumnSortTypeFn {
  numeratorColumnId?: never
  denominatorColumnId?: never
  ratioFn?: never
}

export const roasColumnSortTypeFn =
  ({ subHeader, ratioFn }: ROASColumnSortTypeFn) =>
  <T extends object>(rowA: Row<T>, rowB: Row<T>, columnId: string): number => {
    // Handle aggregated data
    if (rowA.values[columnId]?.revenue == null) {
      const rowAValue = rowA.values[columnId] ?? 0
      const rowBValue = rowB.values[columnId] ?? 0

      if (rowAValue < rowBValue) return -1
      if (rowAValue > rowBValue) return 1
      return 0
    }

    // Handle regular data
    const rowARevenue = rowA.values[columnId].revenue[subHeader]
    const rowAAdSpend = rowA.values[columnId].adSpend[subHeader]
    const rowAROAS = ratioFn(rowARevenue, rowAAdSpend)

    const rowBRevenue = rowB.values[columnId].revenue[subHeader]
    const rowBAdSpend = rowB.values[columnId].adSpend[subHeader]
    const rowBROAS = ratioFn(rowBRevenue, rowBAdSpend)

    if (rowAROAS < rowBROAS) return -1
    if (rowAROAS > rowBROAS) return 1
    return 0
  }

export const cpaColumnSortTypeFn =
  ({ subHeader }: CPAColumnSortTypeFn) =>
  <T extends object>(rowA: Row<T>, rowB: Row<T>, columnId: string): number => {
    // Handle aggregated data
    if (rowA.values[columnId]?.adSpend == null) {
      const rowAValue = rowA.values[columnId] ?? 0
      const rowBValue = rowB.values[columnId] ?? 0

      if (rowAValue < rowBValue) return -1
      if (rowAValue > rowBValue) return 1
      return 0
    }

    // Handle regular data
    const rowACPA = calculateCostPerAction(
      rowA.values[columnId].adSpend[subHeader],
      rowA.values[columnId].conversions[subHeader]
    )
    const rowBCPA = calculateCostPerAction(
      rowB.values[columnId].adSpend[subHeader],
      rowB.values[columnId].conversions[subHeader]
    )

    if (rowACPA < rowBCPA) return -1
    if (rowACPA > rowBCPA) return 1
    return 0
  }

// Proposed columns store { firstHeader, secondHeader }; default sort cannot order those objects.
export const proposedValueColumnSortTypeFn =
  () =>
  <T extends object>(rowA: Row<T>, rowB: Row<T>, columnId: string): number => {
    const rowAValue = (rowA.values[columnId]?.[SECONDSUBHEADER] as number | undefined) ?? 0
    const rowBValue = (rowB.values[columnId]?.[SECONDSUBHEADER] as number | undefined) ?? 0

    if (rowAValue < rowBValue) return -1
    if (rowAValue > rowBValue) return 1
    return 0
  }

const getProposedRatioSortValue = <T extends object>(
  row: Row<T>,
  columnId: string,
  { numeratorColumnId, denominatorColumnId, ratioFn }: CustomRatioDifferenceColumnSortTypeFn
): number => {
  const cellValue = row.values[columnId]
  if (cellValue == null) return 0

  const isAggregated =
    row.isGrouped || (typeof cellValue[SECONDSUBHEADER] === 'number' && cellValue[numeratorColumnId] == null)

  if (isAggregated) {
    return (cellValue[SECONDSUBHEADER] as number) ?? 0
  }

  return ratioFn(
    cellValue[numeratorColumnId][SECONDSUBHEADER] as number,
    cellValue[denominatorColumnId][SECONDSUBHEADER] as number
  )
}

// Proposed ROAS/CPA cells store nested numerator/denominator maps; sort by the proposed ratio.
export const proposedRatioColumnSortTypeFn =
  (props: CustomRatioDifferenceColumnSortTypeFn) =>
  <T extends object>(rowA: Row<T>, rowB: Row<T>, columnId: string): number => {
    const rowAValue = getProposedRatioSortValue(rowA, columnId, props)
    const rowBValue = getProposedRatioSortValue(rowB, columnId, props)

    if (rowAValue < rowBValue) return -1
    if (rowAValue > rowBValue) return 1
    return 0
  }

export const differenceColumnSortTypeFn =
  (props: CustomRatioDifferenceColumnSortTypeFn | BaseDifferenceColumnSortTypeFn = {}) =>
  <T extends object>(rowA: Row<T>, rowB: Row<T>, columnId: string): number => {
    const { numeratorColumnId, denominatorColumnId, ratioFn } = props
    let firstRatio, secondRatio

    // When grouped by channels only, comparing rows passed in are aggregated rows (no nested structure with numerator and denominator), thus should go to 'else' block
    const isAggregated =
      rowA.isGrouped ||
      (rowA.values[columnId] != null &&
        typeof rowA.values[columnId][FIRSTSUBHEADER] === 'number' &&
        typeof rowA.values[columnId][SECONDSUBHEADER] === 'number' &&
        (numeratorColumnId == null || rowA.values[columnId][numeratorColumnId] == null))

    if (numeratorColumnId != null && denominatorColumnId != null && ratioFn != null && !isAggregated) {
      firstRatio = calDifferenceRelativeToPrevious(
        ratioFn(
          rowA.values[columnId][numeratorColumnId][SECONDSUBHEADER],
          rowA.values[columnId][denominatorColumnId][SECONDSUBHEADER]
        ),
        ratioFn(
          rowA.values[columnId][numeratorColumnId][FIRSTSUBHEADER],
          rowA.values[columnId][denominatorColumnId][FIRSTSUBHEADER]
        )
      )

      secondRatio = calDifferenceRelativeToPrevious(
        ratioFn(
          rowB.values[columnId][numeratorColumnId][SECONDSUBHEADER],
          rowB.values[columnId][denominatorColumnId][SECONDSUBHEADER]
        ),
        ratioFn(
          rowB.values[columnId][numeratorColumnId][FIRSTSUBHEADER],
          rowB.values[columnId][denominatorColumnId][FIRSTSUBHEADER]
        )
      )
    } else {
      firstRatio = calDifferenceRelativeToPrevious(
        rowA.values[columnId][SECONDSUBHEADER],
        rowA.values[columnId][FIRSTSUBHEADER]
      )

      secondRatio = calDifferenceRelativeToPrevious(
        rowB.values[columnId][SECONDSUBHEADER],
        rowB.values[columnId][FIRSTSUBHEADER]
      )
    }

    if (firstRatio < secondRatio) return -1
    if (firstRatio > secondRatio) return 1
    return 0
  }

export const reactTableMultiSelectFilter = <T extends object>(
  rows: Array<Row<T>>,
  columnIds: Array<IdType<T>>,
  filterValue: string[]
): Array<Row<T>> => {
  const columnId = columnIds[0]
  return filterValue.length === 0
    ? rows
    : rows.filter((row) => filterValue.some((v) => String(row.values[columnId]).toLowerCase() === v.toLowerCase()))
}
